
import React, { useState, useEffect } from 'react';
import { BookModel, MemberModel, SiteSettingModel, ActiveTab, NoticeModel, InquiryModel, COLLECTIONS } from '../../../models';
import Sidebar, { MenuItem } from '../frame/Sidebar';
import BookManagement from './book/BookManagement';
import SettingsManagement from './settings/SettingsManagement';
import ManualRoot from './manual/ManualRoot';
import NoticeManagement from '../manager/notice/noticeRoot';
import MemberManagement from './member/MemberManagement';
import SuccessRoot from '../manager/success/successRoot';
import AcademyLifeRoot from '../shared/academyLifeRoot';
import TeachersRoot from '../shared/teachersRoot';
import ReviewsRoot from '../shared/reviewsRoot';
import BannersRoot from '../shared/bannersRoot';
import InquiryRoot from '../manager/inquiry/inquiryRoot';
import { auth, db } from '../../../firebase';
import { collection, onSnapshot, query, orderBy, doc, deleteDoc, setDoc, getDoc } from 'firebase/firestore';
import { Building2, BookOpen, ShieldCheck, GraduationCap, Youtube, Settings, Bell, Trophy, Camera, Star, MessageSquare, Sliders } from 'lucide-react';


interface SuperAdminRootProps {
    onGoHome?: () => void;
    currentAdmin: MemberModel;
}

const SuperAdminRoot: React.FC<SuperAdminRootProps> = ({ onGoHome, currentAdmin }) => {
    const [activeTab, setActiveTab] = useState<ActiveTab>('members');
    const [books, setBooks] = useState<BookModel[]>([]);
    const [members, setMembers] = useState<MemberModel[]>([]);
    const [notices, setNotices] = useState<NoticeModel[]>([]);
    const [inquiries, setInquiries] = useState<InquiryModel[]>([]);
    const [editingBook, setEditingBook] = useState<BookModel | null>(null);

    // Global Settings
    const [siteSettings, setSiteSettings] = useState<SiteSettingModel>({
        subjects: [], grades: [], bookTypes: [], publishers: [], examTypes: []
    });

    const [localAdmin, setLocalAdmin] = useState<MemberModel | null>(currentAdmin || null);

    useEffect(() => {
        if (currentAdmin) {
            setLocalAdmin(currentAdmin);
        }
    }, [currentAdmin]);

    useEffect(() => {
        if (localAdmin) return;

        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                try {
                    const docRef = doc(db, COLLECTIONS.MEMBERS, user.uid);
                    const snap = await getDoc(docRef);
                    if (snap.exists()) {
                        setLocalAdmin({ id: snap.id, ...snap.data() } as MemberModel);
                    } else {
                        // Fallback: 임시 관리자 데이터 생성
                        setLocalAdmin({
                            id: user.uid,
                            name: user.displayName || "임시 최고관리자",
                            email: user.email || "",
                            role: "super_admin",
                            status: "active"
                        } as MemberModel);
                    }
                } catch (e) {
                    console.error("Profile recovery error:", e);
                    // Fallback on error
                    setLocalAdmin({
                        id: user.uid,
                        name: user.displayName || "임시 최고관리자",
                        email: user.email || "",
                        role: "super_admin",
                        status: "active"
                    } as MemberModel);
                }
            } else {
                // 로그인 세션이 없는 경우 임시 mock 관리자 주입
                console.log("No authenticated user, injecting mock admin for development...");
                setLocalAdmin({
                    id: "mock_admin_id",
                    name: "임시 최고관리자 (개발용)",
                    email: "admin@test.com",
                    role: "super_admin",
                    status: "active"
                } as MemberModel);
            }
        });
        return () => unsubscribe();
    }, [localAdmin]);

    useEffect(() => {
        if (!localAdmin) return;

        // 1. Books
        const booksQuery = query(collection(db, COLLECTIONS.BOOKS), orderBy('createdAt', 'desc'));
        const unsubscribeBooks = onSnapshot(booksQuery, (snapshot) => {
            const list: BookModel[] = [];
            snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() } as BookModel));
            setBooks(list);
        }, (error) => {
            console.error("Fetch books error:", error);
            alert("교재 목록 로드 실패: " + error.message);
        });

        // 2. Members (For Branch Staff Management?)
        // BranchRoot manages branches and staff. It might need all members to select managers?
        // Or it uses its own query. Let's load members for now just in case.
        const membersQuery = query(collection(db, COLLECTIONS.MEMBERS), orderBy('joinedAt', 'desc'));
        const unsubscribeMembers = onSnapshot(membersQuery, (snapshot) => {
            const list: MemberModel[] = [];
            snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() } as MemberModel));
            setMembers(list);
        });

        // 3. Branches
        // branches listener removed

        // 4. Settings
        const settingsDocRef = doc(db, 'settings', 'site');
        const unsubscribeSettings = onSnapshot(settingsDocRef, (snapshot) => {
            if (snapshot.exists()) setSiteSettings(snapshot.data() as SiteSettingModel);
        });

        // 5. Notices (New)
        const noticesQuery = query(collection(db, COLLECTIONS.NOTICES), orderBy('createdAt', 'desc'));
        const unsubscribeNotices = onSnapshot(noticesQuery, (snapshot) => {
            const list: NoticeModel[] = [];
            snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() } as NoticeModel));
            setNotices(list);
        });

        // 6. Inquiries
        const inquiriesQuery = query(collection(db, COLLECTIONS.INQUIRIES), orderBy('createdAt', 'desc'));
        const unsubscribeInquiries = onSnapshot(inquiriesQuery, (snapshot) => {
            const list: InquiryModel[] = [];
            snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() } as InquiryModel));
            setInquiries(list);
        }, (err) => {
            console.error("Fetch inquiries error:", err);
        });

        return () => {
            unsubscribeBooks();
            unsubscribeMembers();
            unsubscribeSettings();
            unsubscribeNotices();
            unsubscribeInquiries();
        };
    }, [localAdmin]);

    const addMember = async (member: MemberModel) => {
        try { await setDoc(doc(db, COLLECTIONS.MEMBERS, member.id), member); } catch (e) { console.error(e); }
    };

    const deleteMember = async (id: string) => {
        if (!confirm('멤버 정보를 삭제하시겠습니까?')) return;
        try { await deleteDoc(doc(db, COLLECTIONS.MEMBERS, id)); } catch (e) { console.error(e); }
    };

    const renderMainContent = () => {
        switch (activeTab) {
            case 'books':
                return (
                    <BookManagement
                        books={books}
                        siteSettings={siteSettings}
                        editingBook={editingBook}
                        onEditBook={setEditingBook}
                        onDeleteBook={async (b) => {
                            if (confirm(`'${b.title}' 교재를 삭제하시겠습니까?`)) {
                                await deleteDoc(doc(db, COLLECTIONS.BOOKS, b.id));
                            }
                        }}
                        onSubmit={(b) => setDoc(doc(db, COLLECTIONS.BOOKS, b.id), b)}
                        onCancelEdit={() => setEditingBook(null)}
                        currentUserId={currentAdmin?.id}
                        currentUserRole={currentAdmin?.role}
                    />
                );
            case 'members':
                return (
                    <MemberManagement
                        members={members}
                        onUpdateMember={async (m) => {
                            try {
                                await setDoc(doc(db, COLLECTIONS.MEMBERS, m.id), m, { merge: true });
                                alert('권한 정보가 업데이트되었습니다.');
                            } catch (e) {
                                console.error(e);
                                alert('업데이트 실패: ' + e);
                            }
                        }}
                    />
                );
            case 'inquiry': return <InquiryRoot inquiries={inquiries} currentAdmin={localAdmin!} />;
            case 'manuals': return <ManualRoot />;
            case 'settings': return <SettingsManagement />;
            case 'notices': return <NoticeManagement notices={notices} currentAdmin={localAdmin!} />;
            case 'success': return <SuccessRoot />;
            case 'academy-life': return <AcademyLifeRoot />;
            case 'teachers': return <TeachersRoot />;
            case 'reviews': return <ReviewsRoot />;
            case 'banners': return <BannersRoot />;
            default: return null;
        }
    };

    const adminMenuItems: MenuItem[] = [
        { id: 'members', label: '권한관리', icon: ShieldCheck },
        { id: 'banners', label: '배너관리', icon: Sliders },
        { id: 'inquiry', label: '상담관리', icon: MessageSquare },
        { id: 'notices', label: '공지관리', icon: Bell },
        { id: 'success', label: '실적관리', icon: Trophy },
        { id: 'academy-life', label: '학원생활', icon: Camera },
        { id: 'reviews', label: '수강후기', icon: Star },
        { id: 'teachers', label: '강사관리', icon: GraduationCap },
        { id: 'settings', label: '설정관리', icon: Settings },
        { id: 'manuals', label: '매뉴얼관리', icon: Youtube },
        { id: 'books', label: '교재관리', icon: BookOpen },
    ];

    return (
        <div className="flex h-screen w-full bg-slate-50 text-slate-800 overflow-hidden">
            <Sidebar
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                menuItems={adminMenuItems}
                title="Super Admin"
                adminName={localAdmin?.name || "최고 관리자"}
                avatarUrl={localAdmin?.imageUrl}
                onLogout={async () => {
                    await auth.signOut();
                    window.location.href = '/';
                }}
                onGoHome={onGoHome}
                roleLabel="Super Admin"
            />
            <div className="flex-1 h-full overflow-hidden">
                {renderMainContent()}
            </div>
        </div>
    );
};

export default SuperAdminRoot;
